import { format } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Shared Intl.NumberFormat instance for EUR currency (de-DE locale).
 */
const currencyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

/**
 * Format a number as EUR currency string.
 * Example: 1234.5 -> "1.234,50 €"
 */
export function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

/**
 * Shared Intl.NumberFormat for plain numbers (de-DE locale).
 */
const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a number with German decimal/grouping.
 * Example: 1234.5 -> "1.234,50"
 */
export function formatNumber(
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
  if (options) {
    return new Intl.NumberFormat('de-DE', options).format(value);
  }
  return numberFormatter.format(value);
}

/**
 * Format a quantity value for display:
 * integers stay short ("30"), fractional values show up to 3 decimals ("30,5", "1,125").
 */
export function formatQuantity(value: number): string {
  return formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
}

/**
 * Format an ISO date string for display.
 * Default pattern: "dd.MM.yyyy"
 */
export function formatDate(
  dateString: string,
  pattern: string = 'dd.MM.yyyy'
): string {
  return format(new Date(dateString), pattern, { locale: de });
}

/**
 * Format an ISO date string as ISO date (yyyy-MM-dd) for CSV export.
 */
export function formatDateIso(dateString: string): string {
  return format(new Date(dateString), 'yyyy-MM-dd');
}

/**
 * Build the pack-breakdown sub-text used under quantity cells in the
 * **Belegtool** (DocumentLine), where `packageQuantity` is a real persisted
 * number from the original Navision document line.
 *
 * Returns null when no meaningful breakdown exists (fields unset or all 1).
 *
 * Three-tier: Anz × StkProPaket × MengeProPaket unit/VE
 * Two-tier:   Anz × <whichever-is-set> unit/VE
 *
 * For the Materialanfrage (where the user's requested quantity drives the
 * pack count dynamically), use {@link formatRequestPackageBreakdown} instead.
 */
export function formatPackageBreakdown(input: {
  packageQuantity: number | null;
  piecesPerPackage: number | null;
  quantityPerPackage: number | null;
  unit: string | null;
}): string | null {
  const anz = input.packageQuantity;
  const stk = input.piecesPerPackage;
  const menge = input.quantityPerPackage;
  if (anz == null) return null;
  const hasStk = stk != null && stk > 1;
  const hasMenge = menge != null && menge > 1;
  const unit = input.unit ?? '';
  if (hasStk && hasMenge) {
    return `${formatQuantity(anz)} × ${formatQuantity(stk as number)} × ${formatQuantity(menge as number)} ${unit}/VE`;
  }
  if (hasMenge) {
    return `${formatQuantity(anz)} × ${formatQuantity(menge as number)} ${unit}/VE`;
  }
  if (hasStk) {
    return `${formatQuantity(anz)} × ${formatQuantity(stk as number)} ${unit}/VE`;
  }
  return null;
}

/**
 * The Materialanfrage UI captures the user's input as "Anzahl" (pack count),
 * not raw quantity. We persist `quantity` as the total in base unit, so the
 * pack count is always derivable: `quantity / (pieces × menge)`. These helpers
 * keep that derivation in one place so the UI and the email use identical
 * arithmetic.
 *
 * Why a single source of truth: the per-VE size is a real article master
 * datum (`pieces_per_package`, `quantity_per_package`); only the pack count
 * is order-specific and computed.
 */

/**
 * The "Anzahl" (pack count) of a Beleg/document line — the value already shown
 * on the position card as the leading factor of the breakdown
 * ("1 × 250 Stück/VE" → 1). It is the persisted source field `Anz`
 * (`packageQuantity`), not a recomputation.
 *
 * Used when copying a document position into a Materialanfrage: the request
 * input field carries an **Anzahl** (the backend multiplies it by
 * `piecesPerPackage × quantityPerPackage` to the total), so passing the line's
 * total `quantity` would double-multiply. Lines without a packaging structure
 * carry no `Anz`; their total `quantity` is itself the count (no VE
 * multiplication), so we fall back to it, and to 1 as a last resort.
 */
export function getDocumentLineAnzahl(input: {
  packageQuantity: number | null;
  quantity: number | null;
}): number {
  return input.packageQuantity ?? input.quantity ?? 1;
}

/**
 * Multiplier from "Anzahl" (pack count) to total quantity.
 * Returns 1 when no per-package data exists — caller treats input as a direct
 * quantity in that case.
 */
export function getRequestFactor(input: {
  piecesPerPackage: number | null;
  quantityPerPackage: number | null;
}): number {
  const stk = input.piecesPerPackage;
  const menge = input.quantityPerPackage;
  const stkF = stk != null && stk > 0 ? stk : 1;
  const mengeF = menge != null && menge > 0 ? menge : 1;
  return stkF * mengeF;
}

/**
 * Derive "Anzahl" (pack count) for the UI input field from the persisted
 * total `quantity`. Falls back to `quantity` itself when there's no factor
 * (article has no VE master data — input is a direct quantity).
 *
 * Rounded to at least 1, since 0 means "remove item" and is handled elsewhere.
 */
export function getRequestAnzahl(input: {
  quantity: number;
  piecesPerPackage: number | null;
  quantityPerPackage: number | null;
}): number {
  const factor = getRequestFactor(input);
  if (factor <= 1) return input.quantity;
  return Math.max(1, Math.round(input.quantity / factor));
}

/**
 * Build the pack-breakdown sub-text shown under the Anzahl input in the
 * Materialanfrage. Always renders the breakdown dynamically with the current
 * Anzahl — no "à …" fallback, because the UI input is always a pack count
 * and produces a whole-multiple quantity by construction.
 *
 * Examples:
 *   anzahl 1, qty/pkg 4, pcs/pkg 10 (Dachlatten): "1 × 10 × 4 m/VE"
 *   anzahl 2, qty/pkg 75 (Dampfbremse):           "2 × 75 m²/VE"
 *
 * Returns null when the article has no VE master data — caller renders no
 * sub-text (input is then a direct quantity).
 */
export function formatRequestPackageBreakdown(input: {
  quantity: number;
  piecesPerPackage: number | null;
  quantityPerPackage: number | null;
  unit: string | null;
}): string | null {
  const stk = input.piecesPerPackage;
  const menge = input.quantityPerPackage;
  const unit = input.unit ?? '';
  const hasStk = stk != null && stk > 1;
  const hasMenge = menge != null && menge > 1;
  if (!hasStk && !hasMenge) return null;

  const anzahl = getRequestAnzahl(input);
  if (hasStk && hasMenge) {
    return `${formatQuantity(anzahl)} × ${formatQuantity(stk as number)} × ${formatQuantity(menge as number)} ${unit}/VE`;
  }
  if (hasMenge) {
    return `${formatQuantity(anzahl)} × ${formatQuantity(menge as number)} ${unit}/VE`;
  }
  return `${formatQuantity(anzahl)} × ${formatQuantity(stk as number)} ${unit}/VE`;
}
